import React from "react";
import { Link, useLocation } from "react-router-dom";
import ZildLogo from "./ZildLogo";
import {
    PRODUCTS,
    SALES_PHONE_DISPLAY,
    SALES_PHONE_TEL,
    SALES_WHATSAPP_URL,
    SECURITY_PORTAL_URL,
} from "../data/sitePages";
import { getLocaleFromPathname, readLocaleValue, withLocale } from "../lib/locale";

const l = (pt, en) => ({ "pt-BR": pt, "en-US": en });

const footerColumns = (locale) => [
    {
        title: l("Plataforma", "Platform"),
        items: [
            { label: l("Visão geral", "Overview"), to: withLocale("/platform", locale) },
            { label: l("Jornadas de adoção", "Adoption paths"), to: withLocale("/journeys", locale) },
            { label: l("COE de IA", "AI CoE"), to: withLocale("/journeys/ai-coe", locale) },
            { label: l("Indústrias", "Industries"), to: withLocale("/industries", locale) },
            { label: l("Departamentos", "Departments"), to: withLocale("/departments", locale) },
            { label: l("Serviços", "Services"), to: withLocale("/implementation", locale) },
        ],
    },
    {
        title: l("Módulos", "Modules"),
        items: PRODUCTS.map((item) => ({ label: item.title, to: withLocale(item.path, locale) })),
    },
    {
        title: l("Departamentos", "Departments"),
        items: [
            ["/departments#marketing", "Marketing", "Marketing"],
            ["/departments#commercial", "Comercial", "Commercial"],
            ["/departments#sales", "Vendas", "Sales"],
            ["/departments#production", "Produção", "Production"],
            ["/departments#purchasing", "Compras", "Purchasing"],
            ["/departments#inventory", "Estoque", "Inventory"],
            ["/departments#logistics", "Logística", "Logistics"],
            ["/departments#tax", "Fiscal", "Tax"],
            ["/departments#finance", "Financeiro", "Finance"],
            ["/departments#accounting", "Contábil", "Accounting"],
            ["/departments#it", "TI", "IT"],
            ["/departments#hr", "RH", "HR"],
        ].map(([path, pt, en]) => ({ label: l(pt, en), to: withLocale(path, locale) })),
    },
    {
        title: l("Recursos", "Resources"),
        items: [
            { label: l("Guia de Uso Seguro de IA", "Guide to Safe AI Use"), to: withLocale("/guia-de-uso-seguro-de-ia", locale) },
            { label: l("Sobre", "About"), to: withLocale("/about", locale) },
            { label: l("Preços", "Pricing"), to: withLocale("/pricing", locale) },
            { label: l("Estudos e guias", "Studies and guides"), to: withLocale("/studies", locale) },
            { label: l("Documentação", "Documentation"), to: withLocale("/docs", locale) },
            { label: l("Portal de Segurança", "Trust Portal"), href: SECURITY_PORTAL_URL },
            { label: "Blog", to: withLocale("/blog", locale) },
            { label: l("Suporte", "Support"), to: withLocale("/support", locale) },
            { label: l("Privacidade", "Privacy"), to: withLocale("/privacy", locale) },
            { label: l("Termos", "Terms"), to: withLocale("/terms", locale) },
        ],
    },
];

const SOCIAL = [
    { label: "LinkedIn", href: "https://www.linkedin.com/company/zild-ai" },
    { label: "YouTube", href: "https://www.youtube.com/@zild-ai" },
    { label: "Instagram", href: "https://www.instagram.com/zild_ai/" },
    { label: "TikTok", href: "https://www.tiktok.com/@zild.ai" },
    { label: "Facebook", href: "https://www.facebook.com/share/1CPGQERFpZ/" },
];

const SiteFooter = () => {
    const { pathname } = useLocation();
    const locale = getLocaleFromPathname(pathname);
    const cols = footerColumns(locale);

    return (
        <footer
            data-testid="site-footer"
            style={{
                background: "var(--zild-aubergine-nav)",
                color: "rgba(255,255,255,0.78)",
                marginTop: 0,
            }}
        >
            <div className="zild-container" style={{ padding: "72px 24px 32px" }}>
                <div className="zild-footer-grid">
                    <div>
                        <ZildLogo variant="inverse" size={28} />
                        <p
                            style={{
                                marginTop: 20,
                                fontSize: 14,
                                lineHeight: 1.6,
                                color: "rgba(255,255,255,0.6)",
                                maxWidth: 280,
                            }}
                        >
                            {locale === "en-US"
                                ? "Connect processes, people, tools and AI agents to reduce operating costs."
                                : "Integre processos, pessoas, ferramentas e agentes de IA para reduzir custos na operação."}
                        </p>
                        <div className="zild-footer-contact">
                            <a href={SALES_WHATSAPP_URL}>WhatsApp</a>
                            <a href={SALES_PHONE_TEL}>{SALES_PHONE_DISPLAY}</a>
                        </div>
                    </div>

                    {cols.map((col) => (
                        <div key={readLocaleValue(col.title, locale)}>
                            <h2 className="zild-footer-title">{readLocaleValue(col.title, locale)}</h2>
                            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                {col.items.map((it) => {
                                    const label = readLocaleValue(it.label, locale);
                                    const testId = `footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`;
                                    return (
                                        <li key={`${readLocaleValue(col.title, locale)}-${label}`} style={{ marginBottom: 10 }}>
                                            {it.href ? (
                                                <a
                                                    href={it.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={testId}
                                                    className="zild-footer-link"
                                                >
                                                    {label}
                                                </a>
                                            ) : (
                                                <Link to={it.to} data-testid={testId} className="zild-footer-link">
                                                    {label}
                                                </Link>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="zild-footer-bottom">
                    <div>
                        © {new Date().getFullYear()} Zild.ai.{" "}
                        {locale === "en-US" ? "All rights reserved." : "Todos os direitos reservados."}
                    </div>
                    <div>
                        {SOCIAL.map((item) => (
                            <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer">
                                {item.label}
                            </a>
                        ))}
                    </div>
                </div>
            </div>

            <style>{`
                .zild-footer-grid {
                    display: grid;
                    grid-template-columns: 1.35fr repeat(4, 1fr);
                    gap: 42px;
                    margin-bottom: 56px;
                }

                .zild-footer-title {
                    font-family: var(--zild-font-mono);
                    font-size: 11px;
                    letter-spacing: 0.1em;
                    text-transform: uppercase;
                    color: #b9d9d5;
                    margin: 0 0 16px;
                    font-weight: 650;
                    line-height: 1.5;
                }

                [data-testid="site-footer"] a:focus-visible {
                    outline: 3px solid #c4f1df;
                    outline-offset: 4px;
                    border-radius: 3px;
                    text-decoration: underline;
                    text-underline-offset: 4px;
                }

                .zild-footer-link,
                .zild-footer-contact a,
                .zild-footer-bottom a {
                    display: inline-block;
                    min-height: 24px;
                    line-height: 1.7;
                    font-size: 14px;
                    color: rgba(255,255,255,0.78);
                    transition: color 160ms;
                }

                .zild-footer-link:hover,
                .zild-footer-contact a:hover,
                .zild-footer-bottom a:hover {
                    color: rgba(255,255,255,0.98);
                }

                .zild-footer-contact {
                    display: grid;
                    gap: 8px;
                    margin-top: 24px;
                }

                .zild-footer-bottom {
                    border-top: 1px solid rgba(255,255,255,0.08);
                    padding-top: 24px;
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: space-between;
                    gap: 16px;
                    font-size: 12px;
                    color: rgba(255,255,255,0.64);
                }

                .zild-footer-bottom > div:last-child {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 18px;
                }

                .zild-footer-bottom a {
                    font-size: 12px;
                }

                @media (max-width: 1040px) {
                    [data-testid="site-footer"] .zild-footer-grid {
                        grid-template-columns: 1fr 1fr !important;
                    }
                }
                @media (max-width: 600px) {
                    [data-testid="site-footer"] .zild-footer-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
        </footer>
    );
};

export default SiteFooter;
